/**
 * CalendarBase - Abstract base class for calendar implementations
 */

class CalendarBase {
  constructor(options = {}) {
    this.options = {
      language: "en",
      weekStart: 0, // 0 = Sunday, 1 = Monday
      minDate: null,
      maxDate: null,
      disabledDates: [],
      disabledDaysOfWeek: [],
      highlightedDates: [],
      onSelect: null,
      onChange: null,
      ...options,
    };

    this.currentDate = null;
    this.selectedDate = null;
    this.viewDate = null;
    this.container = null;
    this.initialized = false;
  }

  /**
   * Initialize the calendar
   * @param {HTMLElement} container - Container element
   */
  initialize(container) {
    if (!container) {
      throw new Error("Container element is required");
    }

    this.container = container;
    this.currentDate = this.getCurrentDate();
    this.viewDate = { ...this.currentDate };
    this.initialized = true;

    this._render();
    this._attachEventListeners();
  }

  /**
   * Get current date - must be implemented by subclass
   * @abstract
   */
  getCurrentDate() {
    throw new Error("getCurrentDate must be implemented by subclass");
  }

  /**
   * Get month names - must be implemented by subclass
   * @abstract
   */
  getMonthNames() {
    throw new Error("getMonthNames must be implemented by subclass");
  }

  /**
   * Get weekday names - must be implemented by subclass
   * @abstract
   */
  getWeekdayNames() {
    throw new Error("getWeekdayNames must be implemented by subclass");
  }

  /**
   * Get days in month - must be implemented by subclass
   * @abstract
   */
  getDaysInMonth(year, month) {
    throw new Error("getDaysInMonth must be implemented by subclass");
  }

  /**
   * Get first day of month - must be implemented by subclass
   * @abstract
   */
  getFirstDayOfMonth(year, month) {
    throw new Error("getFirstDayOfMonth must be implemented by subclass");
  }

  /**
   * Format date - must be implemented by subclass
   * @abstract
   */
  formatDate(date, format) {
    throw new Error("formatDate must be implemented by subclass");
  }

  /**
   * Navigate to previous month
   */
  prevMonth() {
    this.viewDate.month--;
    if (this.viewDate.month < 1) {
      this.viewDate.month = 12;
      this.viewDate.year--;
    }
    this._render();
    this._triggerChange();
  }

  /**
   * Navigate to next month
   */
  nextMonth() {
    this.viewDate.month++;
    if (this.viewDate.month > 12) {
      this.viewDate.month = 1;
      this.viewDate.year++;
    }
    this._render();
    this._triggerChange();
  }

  /**
   * Navigate to previous year
   */
  prevYear() {
    this.viewDate.year--;
    this._render();
    this._triggerChange();
  }

  /**
   * Navigate to next year
   */
  nextYear() {
    this.viewDate.year++;
    this._render();
    this._triggerChange();
  }

  /**
   * Navigate to today
   */
  today() {
    this.viewDate = { ...this.getCurrentDate() };
    this._render();
    this._triggerChange();
  }

  /**
   * Select a date
   */
  selectDate(date) {
    if (this._isDateDisabled(date)) {
      return;
    }

    this.selectedDate = { ...date };
    this._render();
    this._triggerSelect(date);
  }

  /**
   * Set selected date
   */
  setSelectedDate(date) {
    this.selectedDate = date ? { ...date } : null;
    this._render();
  }

  /**
   * Get selected date
   */
  getSelectedDate() {
    return this.selectedDate ? { ...this.selectedDate } : null;
  }

  /**
   * Set view date
   */
  setViewDate(date) {
    this.viewDate = { ...date };
    this._render();
  }

  /**
   * Check if date is in selected range
   * @protected
   */
  _isDateInRange(date) {
    if (!this._startRangeDate || !this._endRangeDate) return false;

    const start = this._startRangeDate;
    const end = this._endRangeDate;

    return (
      this._compareDates(date, start) >= 0 && this._compareDates(date, end) <= 0
    );
  }

  /**
   * Check if date is start of range
   * @protected
   */
  _isRangeStart(date) {
    return (
      this._startRangeDate &&
      this._compareDates(date, this._startRangeDate) === 0
    );
  }

  /**
   * Check if date is end of range
   * @protected
   */
  _isRangeEnd(date) {
    return (
      this._endRangeDate && this._compareDates(date, this._endRangeDate) === 0
    );
  }

  /**
   * Check if date is disabled
   * @protected
   */
  _isDateDisabled(date) {
    // Check min/max dates
    if (
      this.options.minDate &&
      this._compareDates(date, this.options.minDate) < 0
    ) {
      return true;
    }

    if (
      this.options.maxDate &&
      this._compareDates(date, this.options.maxDate) > 0
    ) {
      return true;
    }

    // Check disabled dates
    for (const disabledDate of this.options.disabledDates) {
      if (this._compareDates(date, disabledDate) === 0) {
        return true;
      }
    }

    // Check disabled days of week
    const dayOfWeek = this._getDayOfWeek(date);
    if (this.options.disabledDaysOfWeek.includes(dayOfWeek)) {
      return true;
    }

    return false;
  }

  /**
   * Check if date is highlighted
   * @protected
   */
  _isDateHighlighted(date) {
    for (const highlightedDate of this.options.highlightedDates) {
      if (this._compareDates(date, highlightedDate) === 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Compare two dates
   * @protected
   */
  _compareDates(date1, date2) {
    if (!date1 || !date2) return -1;
    
    if (date1.year !== date2.year) {
      return date1.year - date2.year;
    }
    if (date1.month !== date2.month) {
      return date1.month - date2.month;
    }
    return date1.day - date2.day;
  }

  /**
   * Get day of week for a date - must be implemented by subclass
   * @abstract
   * @protected
   */
  _getDayOfWeek(date) {
    throw new Error("_getDayOfWeek must be implemented by subclass");
  }

  /**
   * Render the calendar
   * @protected
   */
  _render() {
    if (!this.initialized) return;

    this.container.innerHTML = "";

    // Create calendar structure
    const calendarWrapper = document.createElement("div");
    calendarWrapper.className = "ndtp-calendar";

    // Header
    const header = this._createHeader();
    calendarWrapper.appendChild(header);

    // Days header
    const daysHeader = this._createDaysHeader();
    calendarWrapper.appendChild(daysHeader);

    // Days grid
    const daysGrid = this._createDaysGrid();
    calendarWrapper.appendChild(daysGrid);

    this.container.appendChild(calendarWrapper);
  }

  /**
   * Create calendar header
   * @protected
   */
  _createHeader() {
    const header = document.createElement("div");
    header.className = "ndtp-calendar-header";

    // Previous month button
    const prevBtn = document.createElement("button");
    prevBtn.className = "ndtp-btn ndtp-btn-prev-month";
    prevBtn.innerHTML = "‹";
    prevBtn.type = "button";
    prevBtn.onclick = (e) => {
      e.stopPropagation();
      this.prevMonth();
    };

    // Month/Year display
    const monthYear = document.createElement("div");
    monthYear.className = "ndtp-month-year";

    const monthSelect = this._createMonthSelect();
    const yearSelect = this._createYearSelect();

    monthYear.appendChild(monthSelect);
    monthYear.appendChild(yearSelect);

    // Next month button
    const nextBtn = document.createElement("button");
    nextBtn.className = "ndtp-btn ndtp-btn-next-month";
    nextBtn.innerHTML = "›";
    nextBtn.type = "button";
    nextBtn.onclick = (e) => {
      e.stopPropagation();
      this.nextMonth();
    };

    header.appendChild(prevBtn);
    header.appendChild(monthYear);
    header.appendChild(nextBtn);

    return header;
  }

  /**
   * Create month select
   * @protected
   */
  _createMonthSelect() {
    const select = document.createElement("select");
    select.className = "ndtp-month-select";

    const monthNames = this.getMonthNames();
    monthNames.forEach((name, index) => {
      const option = document.createElement("option");
      option.value = index + 1;
      option.textContent = name;
      if (index + 1 === this.viewDate.month) {
        option.selected = true;
      }
      select.appendChild(option);
    });

    select.onclick = (e) => {
      e.stopPropagation();
    };

    select.onchange = (e) => {
      e.stopPropagation();
      this.viewDate.month = parseInt(e.target.value, 10);
      this._render();
      this._triggerChange();
    };

    return select;
  }

  /**
   * Create year select
   * @protected
   */
  _createYearSelect() {
    const select = document.createElement("select");
    select.className = "ndtp-year-select";

    const currentYear = this.viewDate.year;
    const startYear = currentYear - 50;
    const endYear = currentYear + 50;

    for (let year = startYear; year <= endYear; year++) {
      const option = document.createElement("option");
      option.value = year;
      option.textContent = year;
      if (year === currentYear) {
        option.selected = true;
      }
      select.appendChild(option);
    }

    select.onclick = (e) => {
      e.stopPropagation();
    };

    select.onchange = (e) => {
      e.stopPropagation();
      this.viewDate.year = parseInt(e.target.value, 10);
      this._render();
      this._triggerChange();
    };

    return select;
  }

  /**
   * Create days header
   * @protected
   */
  _createDaysHeader() {
    const daysHeader = document.createElement("div");
    daysHeader.className = "ndtp-days-header";

    const weekdays = this.getWeekdayNames();
    const startIndex = this.options.weekStart;

    for (let i = 0; i < 7; i++) {
      const weekdayIndex = (startIndex + i) % 7;
      const dayHeader = document.createElement("div");
      dayHeader.className = "ndtp-day-header";
      dayHeader.textContent = weekdays[weekdayIndex].substring(0, 3);
      daysHeader.appendChild(dayHeader);
    }

    return daysHeader;
  }

  /**
   * Create days grid - must be implemented by subclass
   * @abstract
   * @protected
   */
  _createDaysGrid() {
    throw new Error("_createDaysGrid must be implemented by subclass");
  }

  // Update the _createDayCell method to handle range selection and fix class inconsistency
  _createDayCell(date, isCurrentMonth = true) {
    const cell = document.createElement("div");
    cell.className = "ndtp-day";

    // Store date data on the element
    cell.dataset.date = JSON.stringify(date);

    if (!isCurrentMonth) {
      cell.classList.add("ndtp-other-month");
    }

    if (this._isDateDisabled(date)) {
      cell.classList.add("ndtp-disabled");
    }

    if (this._isDateHighlighted(date)) {
      cell.classList.add("ndtp-highlighted");
    }

    // Range selection classes
    if (this._isRangeStart && this._isRangeStart(date)) {
      cell.classList.add("ndtp-range-start");
    }

    if (this._isRangeEnd && this._isRangeEnd(date)) {
      cell.classList.add("ndtp-range-end");
    }

    if (this._isDateInRange && this._isDateInRange(date)) {
      cell.classList.add("ndtp-in-range");
    }

    // Fix inconsistent class naming - use ndtp-selected instead of ndp-selected
    if (
      this.selectedDate &&
      this._compareDates(date, this.selectedDate) === 0
    ) {
      cell.classList.add("ndtp-selected");
    }

    // Check if this is today's date
    if (this._compareDates(date, this.currentDate) === 0) {
      cell.classList.add("ndtp-today");
    }

    cell.textContent = date.day;

    // Add click handler unless disabled
    if (!this._isDateDisabled(date)) {
      cell.onclick = (e) => {
        e.stopPropagation();
        this.selectDate(date);
      };
    }

    return cell;
  }

  /**
   * Attach event listeners
   * @protected
   */
  _attachEventListeners() {
    // Add keyboard navigation
    this.container.addEventListener("keydown", (e) => {
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          // Move to previous day
          break;
        case "ArrowRight":
          e.preventDefault();
          // Move to next day
          break;
        case "ArrowUp":
          e.preventDefault();
          // Move to previous week
          break;
        case "ArrowDown":
          e.preventDefault();
          // Move to next week
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          // Select focused date
          break;
        case "Escape":
          e.preventDefault();
          // Close calendar (if in popup mode)
          break;
      }
    });
  }

  /**
   * Trigger change event
   * @protected
   */
  _triggerChange() {
    if (this.options.onChange) {
      this.options.onChange(this.viewDate);
    }
  }

  /**
   * Trigger select event
   * @protected
   */
  _triggerSelect(date) {
    if (this.options.onSelect) {
      this.options.onSelect(date);
    }
  }

  /**
   * Destroy the calendar
   */
  destroy() {
    if (this.container) {
      this.container.innerHTML = "";
    }
    this.initialized = false;
  }
}

export default CalendarBase;